import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { formatNumber, parseLocaleNumber } from '../../utils/numberFormatter';
import { trackAuthorizationModalOpened, trackAuthorizationRequested, trackAuthorizationRequestFailed } from '../../analytics/events';
import { useAnalyticsContext } from '../../analytics/useAnalyticsContext';
import api from '../../services/api';

// API call for list of exception types
const fetchExceptionTypes = async () => {
  try {
    const response = await api.get('/override-exception-types');
    return response.data.map(type => ({
      id: type.id,
      label: type.name,
    }));
  } catch (error) {
    console.error('Error al obtener tipos de excepción:', error);
    return [
      { id: 1, label: 'Error de datos' },
      { id: 2, label: 'Cliente VIP' },
      { id: 3, label: 'Situación particular' },
    ];
  }
};

const CUOTAS_OPTIONS = [1, 3, 6, 9, 12, 18];

const UnifiedAuthorizationModal = ({
  show,
  onClose,
  onRequestAuthorization,
  onRequestException,
  initialAmount,
  customerDni,
  reason,
}) => {
  // Determinar qué tipos están habilitados
  const authorizationsEnabled = process.env.REACT_APP_ENABLE_AUTHORIZATIONS === 'true';
  const exceptionsEnabled = process.env.REACT_APP_ENABLE_OVERRIDE_EXCEPTIONS === 'true';
  const bothEnabled = authorizationsEnabled && exceptionsEnabled;
  
  // Tipo de solicitud: 'authorization' o 'exception'
  // Si solo una está habilitada, usar esa directamente
  const getInitialRequestType = () => {
    if (authorizationsEnabled && !exceptionsEnabled) return 'authorization';
    if (exceptionsEnabled && !authorizationsEnabled) return 'exception';
    return 'authorization'; // Por defecto si ambas están habilitadas
  };
  
  const [requestType, setRequestType] = useState(getInitialRequestType());
  
  // Campos comunes
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [observation, setObservation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Campos específicos de Autorización
  const [password, setPassword] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  // Campos específicos de Excepción
  const [cuotas, setCuotas] = useState('');
  const [exceptionType, setExceptionType] = useState('');
  const [exceptionTypes, setExceptionTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  
  const { config } = useAuth();
  const { commerceId, branchId } = useAnalyticsContext();

  // Cargar tipos de excepción cuando se abre el modal
  useEffect(() => {
    if (show) {
      setTypesLoading(true);
      fetchExceptionTypes()
        .then((types) => {
          setExceptionTypes(types);
        })
        .finally(() => setTypesLoading(false));
    }
  }, [show]);

  // Reset form state on open
  useEffect(() => {
    if (show) {
      if (initialAmount) {
        setAmount(initialAmount.toString());
        setDisplayAmount(formatNumber(initialAmount));
      } else {
        setAmount('');
        setDisplayAmount('');
      }
      setObservation('');
      setPassword('');
      setAttachment(null);
      setCuotas('');
      setExceptionType('');
      setIsLoading(false);
      setRequestType(getInitialRequestType()); // Usar el tipo inicial según configuración
      
      // Track authorization modal opened
      if (commerceId) {
        trackAuthorizationModalOpened({
          commerceId,
          branchId,
          customerDni,
          requestedAmount: initialAmount ? parseFloat(initialAmount) : undefined,
          reason: reason || 'manual_request'
        });
      }
    }
  }, [show, initialAmount, commerceId, branchId, customerDni, reason]);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    
    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      return;
    }

    const numericValue = parseLocaleNumber(value);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toString());
      setDisplayAmount(formatNumber(numericValue));
    }
  };

  const handleFileChange = (e) => {
    setAttachment(e.target.files[0]);
  };

  const handleRequest = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      if (requestType === 'authorization') {
        const result = await onRequestAuthorization({ 
          amount, 
          observation, 
          password, 
          attachment 
        });
        
        // Track authorization requested successfully
        if (commerceId) {
          const amountValue = parseFloat(amount);
          const authorizationStatus = result?.authorization?.status || result?.data?.authorization?.status;
          
          trackAuthorizationRequested({
            commerceId,
            branchId,
            customerDni,
            amount: amountValue,
            hasAttachment: !!attachment,
            requiresPassword: !!password,
            authorizationStatus: authorizationStatus
          });
        }
      } else {
        // Exception request
        await onRequestException({
          amount,
          cuotas: cuotas || CUOTAS_OPTIONS[0], // Si está vacío, usar el primer valor por defecto
          tipo: exceptionType,
          observaciones: observation,
        });
      }
    } catch (error) {
      // Track authorization request failed (solo para autorizaciones)
      if (requestType === 'authorization' && commerceId) {
        const amountValue = parseFloat(amount);
        let errorCode = undefined;
        let errorMessage = undefined;
        
        if (error.response) {
          errorCode = error.response.status?.toString();
          errorMessage = error.response.data?.message || error.message;
        } else {
          errorMessage = error.message;
        }
        
        trackAuthorizationRequestFailed({
          commerceId,
          branchId,
          customerDni,
          amount: amountValue,
          errorCode,
          errorMessage
        });
      }
      
      console.error(`Error en la solicitud de ${requestType === 'authorization' ? 'autorización' : 'excepción'}:`, error);
      setIsLoading(false);
      throw error;
    }
  };

  const shouldShowAdminAuthorizationAlert = () => {
    if (requestType !== 'authorization') return false;
    
    const amountValue = parseFloat(amount);
    const maxAmount = config.self_authorization?.max_amount;
    
    if (!config.self_authorization?.self_authorization) {
      return true;
    }

    if (maxAmount === null || maxAmount === false) {
      return false;
    }

    return amountValue > maxAmount;
  };

  const shouldShowMerchantPassword = () => {
    if (requestType !== 'authorization') return false;
    
    const amountValue = parseFloat(amount);
    const minAmount = config.self_authorization?.min_amount_for_authentication;
    const maxAmount = config.self_authorization?.max_amount;

    if (!config.self_authorization || minAmount === false) {
      return false;
    }

    return (
      minAmount !== null &&
      amountValue > minAmount &&
      (maxAmount === false || maxAmount === null || amountValue <= maxAmount)
    );
  };

  const isValid = () => {
    if (!amount || parseFloat(amount) <= 0) return false;
    if (!observation || observation.trim().length === 0) return false;
    
    if (requestType === 'authorization') {
      // Para autorización solo necesita monto y observación
      return true;
    } else {
      // Para excepción necesita también el tipo
      return !!exceptionType;
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {bothEnabled 
            ? 'Solicitar Autorización' 
            : requestType === 'authorization' 
              ? 'Solicitar Autorización' 
              : 'Solicitud de Excepción'
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Selector de tipo de solicitud - Solo mostrar si ambas opciones están habilitadas */}
        {bothEnabled && (
          <div className="mb-4">
            <label className="form-label fw-bold mb-2">
              ¿Qué tipo de autorización necesitas?
            </label>
            <div className="d-flex gap-2 p-2 bg-light rounded">
              <button
                type="button"
                className={`btn flex-fill ${
                  requestType === 'authorization' 
                    ? 'btn-primary' 
                    : 'btn-outline-primary'
                }`}
                onClick={() => {
                  if (requestType !== 'authorization') {
                    setExceptionType('');
                    setCuotas('');
                    setRequestType('authorization');
                  }
                }}
                disabled={isLoading}
                style={{
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="bi bi-check-circle me-2"></i>
                <div className="d-flex flex-column align-items-center">
                  <span>Autorización</span>
                  <small style={{ fontSize: '0.7rem', opacity: 0.9 }}>Solo más crédito</small>
                </div>
              </button>
              <button
                type="button"
                className={`btn flex-fill ${
                  requestType === 'exception' 
                    ? 'btn-warning' 
                    : 'btn-outline-warning'
                }`}
                onClick={() => {
                  if (requestType !== 'exception') {
                    setPassword('');
                    setAttachment(null);
                    setRequestType('exception');
                  }
                }}
                disabled={isLoading}
                style={{
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="bi bi-star me-2"></i>
                <div className="d-flex flex-column align-items-center">
                  <span>Excepción</span>
                  <small style={{ fontSize: '0.7rem', opacity: 0.9 }}>Planes especiales</small>
                </div>
              </button>
            </div>
            
            {/* Descripción del tipo seleccionado */}
            <div className={`mt-3 p-2 rounded ${
              requestType === 'authorization' 
                ? 'bg-light-info border-start border-info border-3' 
                : 'bg-light-warning border-start border-warning border-3'
            }`}>
              {requestType === 'authorization' ? (
                <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="authorization-tooltip" className="text-wrap" style={{ maxWidth: '300px' }}>
                        <div className="text-start">
                          Solo necesitas extender el monto disponible. El cliente mantendrá sus planes y categorías actuales.
                          <br /><br />
                          <strong>Usa esto cuando:</strong> El cliente necesita más crédito pero puede pagar con los planes normales.
                        </div>
                      </Tooltip>
                    }
                  >
                    <strong className="text-info d-flex align-items-center mb-0" style={{ cursor: 'help' }}>
                      <i className="bi bi-info-circle me-2 mb-2"></i>
                      Autorización Simple
                    </strong>
                  </OverlayTrigger>
              ) : (
                <div>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="exception-tooltip" className="text-wrap" style={{ maxWidth: '300px' }}>
                        <div className="text-start">
                          Necesitas condiciones especiales: diferentes cuotas, tasas especiales, o vender a un cliente rechazado.
                          <br /><br />
                          <strong>Usa esto cuando:</strong> Necesitas planes personalizados o el cliente fue rechazado pero quieres hacer una oferta especial.
                        </div>
                      </Tooltip>
                    }
                  >
                    <strong className="text-warning d-flex align-items-center mb-0" style={{ cursor: 'help' }}>
                      <i className="bi bi-exclamation-triangle me-2 mb-2"></i>
                      Excepción Especial
                    </strong>
                  </OverlayTrigger>
                </div>
              )}
            </div>

            {requestType !== 'authorization' && (
              <p className="mt-3 mb-0 ms-4 me-4 text-muted small">
                Completá esta solicitud para que un supervisor evalúe si se puede autorizar igual.{' '}
                <strong>Si la aprueba, vas a poder hacer la solicitud normalmente.</strong>
              </p>
            )}
          </div>
        )}
        
        {/* Badge informativo cuando solo una opción está disponible */}
        {!bothEnabled && (
          <div className={`mb-4 p-2 rounded ${
            requestType === 'authorization' 
              ? 'bg-light-info border-start border-info border-3' 
              : 'bg-light-warning border-start border-warning border-3'
          }`}>
            {requestType === 'authorization' ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="authorization-tooltip" className="text-wrap" style={{ maxWidth: '300px' }}>
                    <div className="text-start">
                      Solo necesitas extender el monto disponible. El cliente mantendrá sus planes y categorías actuales.
                      <br /><br />
                      <strong>Usa esto cuando:</strong> El cliente necesita más crédito pero puede pagar con los planes normales.
                    </div>
                  </Tooltip>
                }
              >
                <strong className="text-info d-flex align-items-center mb-0" style={{ cursor: 'help' }}>
                  <i className="bi bi-info-circle me-2 mb-2"></i>
                  Autorización Simple
                </strong>
              </OverlayTrigger>
            ) : (
              <div>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="exception-tooltip" className="text-wrap" style={{ maxWidth: '300px' }}>
                      <div className="text-start">
                        Necesitas condiciones especiales: diferentes cuotas, tasas especiales, o vender a un cliente rechazado.
                        <br /><br />
                        <strong>Usa esto cuando:</strong> Necesitas planes personalizados o el cliente fue rechazado pero quieres hacer una oferta especial.
                      </div>
                    </Tooltip>
                  }
                >
                  <strong className="text-warning d-flex align-items-center mb-0" style={{ cursor: 'help' }}>
                    <i className="bi bi-exclamation-triangle me-2 mb-2"></i>
                    Excepción Especial
                  </strong>
                </OverlayTrigger>
                <p className="mt-3 mb-0 text-muted small">
                  Completá esta solicitud para que un supervisor evalúe si se puede autorizar igual.{' '}
                  <strong>Si la aprueba, vas a poder hacer la solicitud normalmente.</strong>
                </p>
              </div>
            )}
          </div>
        )}

        <Form>
          {/* Monto - Campo común */}
          <Form.Group controlId="amount" className="mt-1">
            <Form.Label>
              Monto Solicitado <span className="text-danger">*</span>
            </Form.Label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <Form.Control
                type="text"
                placeholder={requestType === 'exception' ? 'Ej: 150000' : 'Ingrese el monto'}
                autoComplete="off"
                value={displayAmount}
                onChange={handleAmountChange}
              />
            </div>
            {requestType === 'exception' && (
              <Form.Text className="text-muted">
                Ingresá el monto necesario para poder cerrar la solicitud.
              </Form.Text>
            )}
          </Form.Group>

          {/* Campos específicos de Excepción */}
          {requestType === 'exception' && (
            <>
              {/* Cuotas */}
              <Form.Group controlId="cuotas" className="mt-3">
                <Form.Label>
                  Cuotas deseadas{' '}
                  <span className="text-small text-muted">(opcional)</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="24"
                  placeholder="Ej: 12"
                  value={cuotas || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setCuotas('');
                    } else {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 1 && numValue <= 24) {
                        setCuotas(numValue);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setCuotas('');
                    } else {
                      const numValue = parseInt(value, 10);
                      if (isNaN(numValue) || numValue < 1) {
                        setCuotas('');
                      } else if (numValue > 24) {
                        setCuotas(24);
                      } else {
                        setCuotas(numValue);
                      }
                    }
                  }}
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.9em' }}>
                  Si el cliente te pidió un plan en particular, elegilo acá. Es solo una referencia: el supervisor arma los planes finales.
                </Form.Text>
              </Form.Group>

              {/* Tipo de Excepción */}
              <Form.Group controlId="exceptionType" className="mt-3">
                <Form.Label>
                  ¿Por qué estás pidiendo la excepción? <span className="text-danger">*</span>
                </Form.Label>
                {typesLoading ? (
                  <div>
                    <Spinner animation="border" size="sm" /> Cargando tipos...
                  </div>
                ) : (
                  <Form.Select
                    value={exceptionType}
                    onChange={(e) => setExceptionType(e.target.value)}
                  >
                    <option value="">Seleccioná un tipo</option>
                    {exceptionTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </>
          )}

          {/* Observaciones - Campo común */}
          <Form.Group controlId="observation" className="mt-3">
            <Form.Label>
              {requestType === 'authorization' ? 'Información Adicional' : 'Motivo de la excepción'}{' '}
              <span className="text-danger">*</span>
            </Form.Label>
            {requestType === 'exception' && (
              <Form.Text className="text-muted d-block mb-2">
                Explicános por qué creés que este cliente puede cumplir con el pago.
              </Form.Text>
            )}
            <div style={{ position: 'relative' }}>
              <Form.Control
                as="textarea"
                rows={requestType === 'exception' ? 4 : 3}
                placeholder={
                  requestType === 'authorization'
                    ? 'Por favor, ingresa el motivo por el cual se solicita la autorización'
                    : 'Ej: relación laboral estable; ingreso fijo; solicitud seguido en el local, Etc'
                }
                value={observation}
                onChange={(e) => setObservation(e.target.value.slice(0, 255))}
                maxLength={255}
                style={requestType === 'exception' ? {
                  paddingRight: '60px'
                } : {}}
              />
              {requestType === 'exception' && (
                <small 
                  className="text-muted"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '12px',
                    fontSize: '0.875rem'
                  }}
                >
                  {observation.length}/255
                </small>
              )}
            </div>
            <Form.Text className="text-muted">
              {requestType === 'exception' 
                ? 'Contá brevemente por qué debería aprobarse. Máximo 255 caracteres.'
                : 'Máximo 255 caracteres'
              }
            </Form.Text>
          </Form.Group>

          {/* Campos específicos de Autorización */}
          {requestType === 'authorization' && (
            <>
              {/* Contraseña (condicional) */}
              {shouldShowMerchantPassword() && (
                <Form.Group controlId="password" className="mt-3">
                  <Form.Label>Contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Ingrese su contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    Se requiere contraseña para montos superiores a ${formatNumber(config.self_authorization?.min_amount_for_authentication)}
                  </Form.Text>
                </Form.Group>
              )}
              
              {/* Archivo Adjunto */}
              <Form.Group controlId="attachment" className="mt-3">
                <Form.Label>Archivo Adjunto (Opcional)</Form.Label>
                <div
                  className={`attachment-dropzone ${attachment ? 'uploaded' : ''}`}
                  onClick={() => document.getElementById('attachment').click()}
                  style={{ 
                    userSelect: 'none',
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: attachment ? '#f8f9fa' : 'white',
                    width: '100%'
                  }}
                >
                  <span className="attachment-icon">
                    <i className="bi bi-file-earmark-text fs-4"></i>
                  </span>
                  <div className="attachment-name flex-grow-1 mt-2">
                    {attachment ? attachment.name : 'Arrastra el archivo aquí o haz clic para seleccionarlo'}
                  </div>
                  <span className="attachment-status mt-2">
                    {attachment ? (
                      <i className="bi bi-check-circle-fill text-success"></i>
                    ) : null }
                  </span>
                  <input
                    type="file"
                    id="attachment"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
                <Form.Text className="text-muted">
                  Formatos aceptados: PDF, JPG, PNG
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Form>

        {/* Alertas informativas */}
        {shouldShowAdminAuthorizationAlert() && (
          <div className="mt-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
            <span className="text-secondary">
              Esta solicitud deberá ser autorizada por un administrador.
            </span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleRequest}
          disabled={!isValid() || isLoading || (requestType === 'exception' && typesLoading)}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Cargando...
            </>
          ) : (
            'Enviar Solicitud'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnifiedAuthorizationModal;

